import { useState, useMemo, useCallback, useEffect } from 'react';
import type { MockInspection, MockInspectionResult } from '@/mocks/qms';
import { getAllInspectionChecklists } from '@/api/inspectionchecklist.api';
import { getAllQualityParameters } from '@/api/qualityparameter.api';

interface Props {
  inspection: MockInspection | null;
  existingResults: MockInspectionResult[];
  onClose: () => void;
  onSubmit: (results: MockInspectionResult[], overallStatus: 'PASSED' | 'FAILED') => void;
}

type ObservedState = Record<string, { value: string; notes: string; manualStatus?: 'PASS' | 'FAIL' | 'NA' }>;

interface ChecklistState {
  id: string;
  name: string;
  parameters: string[];
}

interface QualityParameterState {
  id: string;
  name: string;
  code: string;
  type: string;
  unit: string | null;
  minValue: number | null;
  maxValue: number | null;
}

function computeNumericStatus(
  val: string,
  min: number | null,
  max: number | null
): 'PASS' | 'FAIL' | 'NA' {
  const num = parseFloat(val);
  if (Number.isNaN(num)) return 'NA';
  if (min !== null && num < min) return 'FAIL';
  if (max !== null && num > max) return 'FAIL';
  if (min === null && max === null) return 'PASS';
  return 'PASS';
}

export default function InspectionFormModal({ inspection, existingResults, onClose, onSubmit }: Props) {
  const [checklists, setChecklists] = useState<ChecklistState[]>([]);
  const [qualityParameters, setQualityParameters] = useState<QualityParameterState[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    async function loadData() {
      try {
        setLoading(true);
        const [checklistsRes, qpRes] = await Promise.all([
          getAllInspectionChecklists(),
          getAllQualityParameters(),
        ]);
        if (!active) return;

        if (checklistsRes.success && checklistsRes.data) {
          const mappedChecklists: ChecklistState[] = checklistsRes.data.map((c) => ({
            id: c.id,
            name: c.name,
            parameters: c.parameter_ids || [],
          }));
          setChecklists(mappedChecklists);
        }

        if (qpRes.success && qpRes.data) {
          const mappedQPs: QualityParameterState[] = qpRes.data.map((qp) => ({
            id: qp.id,
            name: qp.name,
            code: qp.code,
            type: qp.type,
            unit: qp.unit,
            minValue: qp.min_value !== null ? Number(qp.min_value) : null,
            maxValue: qp.max_value !== null ? Number(qp.max_value) : null,
          }));
          setQualityParameters(mappedQPs);
        }
      } catch (err) {
        console.error('Failed to load checklists or quality parameters in InspectionFormModal:', err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const checklist = useMemo(() => {
    if (!inspection) return null;
    return checklists.find((c) =>
      (inspection.checklistId && String(c.id).toLowerCase() === String(inspection.checklistId).toLowerCase()) ||
      (inspection.checklistName && c.name?.toLowerCase().trim() === inspection.checklistName?.toLowerCase().trim())
    );
  }, [inspection, checklists]);

  const parameters = useMemo(() => {
    if (!checklist) {
      if (existingResults && existingResults.length > 0) {
        return existingResults.map((r) => ({
          id: r.parameterId || r.id,
          name: r.parameterName,
          code: (r as any).parameterCode || '',
          type: r.parameterType || 'PASS_FAIL',
          unit: r.unit || null,
          minValue: r.minValue !== undefined && r.minValue !== null ? Number(r.minValue) : null,
          maxValue: r.maxValue !== undefined && r.maxValue !== null ? Number(r.maxValue) : null,
        }));
      }
      return [];
    }
    return checklist.parameters
      .map((pid) => qualityParameters.find((p) => String(p.id) === String(pid)))
      .filter(Boolean)
      .map((p) => p!);
  }, [checklist, qualityParameters, existingResults]);

  const initialObserved = useMemo(() => {
    const map: ObservedState = {};
    parameters.forEach((p) => {
      const existing = existingResults.find((r) => r.parameterId === p.id);
      map[p.id] = {
        value: existing?.observedValue || '',
        notes: existing?.notes || '',
        manualStatus: existing?.status || undefined,
      };
    });
    return map;
  }, [parameters, existingResults]);

  const [observed, setObserved] = useState<ObservedState>(initialObserved);
  const [batchNo] = useState(inspection?.batchNumber || '');
  const [lotNo] = useState(inspection?.lotNumber || '');

  useEffect(() => {
    if (Object.keys(initialObserved).length > 0) {
      setObserved(initialObserved);
    }
  }, [initialObserved]);

  const handleValueChange = useCallback((paramId: string, value: string) => {
    setObserved((prev) => {
      const next = { ...prev, [paramId]: { ...prev[paramId], value } };
      return next;
    });
  }, []);

  const handleNotesChange = useCallback((paramId: string, notes: string) => {
    setObserved((prev) => ({ ...prev, [paramId]: { ...prev[paramId], notes } }));
  }, []);

  const handleManualStatus = useCallback((paramId: string, status: 'PASS' | 'FAIL' | 'NA') => {
    setObserved((prev) => ({ ...prev, [paramId]: { ...prev[paramId], manualStatus: status } }));
  }, []);

  const getParamStatus = (param: (typeof parameters)[number], state: ObservedState[string]): 'PASS' | 'FAIL' | 'NA' => {
    if (state?.manualStatus) return state.manualStatus;
    if (param.type === 'NUMERIC') {
      return computeNumericStatus(state?.value || '', param.minValue, param.maxValue);
    }
    if (param.type === 'PASS_FAIL') {
      const v = state?.value?.toUpperCase();
      if (v === 'PASS') return 'PASS';
      if (v === 'FAIL') return 'FAIL';
      return 'NA';
    }
    return state?.value ? 'PASS' : 'NA';
  };

  const [manualOverall, setManualOverall] = useState<'PASSED' | 'FAILED' | null>(null);

  const calculatedOverall: 'PASSED' | 'FAILED' = useMemo(() => {
    if (parameters.length === 0) return 'PASSED';
    const statuses = parameters.map((p) => getParamStatus(p, observed[p.id]));
    if (statuses.some((s) => s === 'FAIL')) return 'FAILED';
    if (statuses.every((s) => s === 'PASS')) return 'PASSED';
    return 'FAILED';
  }, [parameters, observed]);

  const overallStatus = manualOverall || calculatedOverall;

  const isComplete = useMemo(() => {
    if (manualOverall !== null) return true;
    if (parameters.length === 0) return true;
    return parameters.every((p) => {
      const st = observed[p.id];
      if (p.type === 'PASS_FAIL') return st?.value === 'PASS' || st?.value === 'FAIL';
      if (p.type === 'NUMERIC') return st?.value !== '' && !Number.isNaN(parseFloat(st?.value || ''));
      return true;
    });
  }, [parameters, observed, manualOverall]);

  const handleSubmit = () => {
    if (!inspection) return;
    const results: MockInspectionResult[] = parameters.map((p, idx) => {
      const st = observed[p.id];
      const status = getParamStatus(p, st);
      return {
        id: `ir-${Date.now()}-${idx}`,
        inspectionId: inspection.id,
        parameterId: p.id,
        parameterName: p.name,
        parameterType: p.type,
        unit: p.unit,
        minValue: p.minValue,
        maxValue: p.maxValue,
        observedValue: st?.value || '',
        status,
        notes: st?.notes || null,
      };
    });
    onSubmit(results, overallStatus);
  };

  if (!inspection) return null;

  const typeLabel =
    inspection.type === 'INCOMING'
      ? 'Incoming'
      : inspection.type === 'IN_PROCESS'
      ? 'In-Process'
      : 'Final';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {typeLabel} Inspection — {inspection.inspectionNumber}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {inspection.itemName} {inspection.variantName ? `(${inspection.variantName})` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {inspection.sourceType === 'GRN' && (
          <div className="mx-6 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-information-line text-blue-600 text-lg" />
              <div>
                <span className="font-semibold text-blue-900">
                  GRN: {inspection.sourceNumber} &bull; Item: {inspection.itemName}
                </span>
                <p className="text-xs text-blue-700">
                  Passing this inspection will add stock <strong>only for this item</strong> ({inspection.totalQty} {inspection.unit}). Other items in this GRN remain pending until individually inspected.
                </p>
              </div>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 bg-blue-100 text-blue-800 rounded border border-blue-200 shrink-0">
              Item QC
            </span>
          </div>
        )}

        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-2">
          <div>
            <span className="text-gray-500">Source:</span>
            <p className="font-medium text-gray-900">{inspection.sourceNumber}</p>
          </div>
          <div>
            <span className="text-gray-500">Batch / Lot:</span>
            <p className="font-medium text-gray-900">
              {batchNo || lotNo ? `${batchNo || ''} ${lotNo || ''}`.trim() : '—'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Sample Plan:</span>
            <p className="font-medium text-gray-900">{inspection.samplingPlan.replace('_', ' ')}</p>
          </div>
          <div>
            <span className="text-gray-500">Sample Qty:</span>
            <p className="font-medium text-gray-900">
              {inspection.sampleQty} {inspection.unit}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Inspection Parameters</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 w-48">Parameter</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 w-32">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 w-40">Required Range</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Observed Value</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 w-24">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {parameters.map((param) => {
                  const st = observed[param.id] || { value: '', notes: '' };
                  const status = getParamStatus(param, st);
                  const isOutOfRange = param.type === 'NUMERIC' && status === 'FAIL' && st.value !== '';

                  return (
                    <tr key={param.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{param.name}</p>
                        {st.notes && <p className="text-xs text-gray-400 mt-1">{st.notes}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{param.type.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {param.type === 'NUMERIC' ? (
                          <span>
                            {param.minValue !== null && param.maxValue !== null
                              ? `${param.minValue} — ${param.maxValue} ${param.unit || ''}`
                              : param.minValue !== null
                              ? `≥ ${param.minValue} ${param.unit || ''}`
                              : param.maxValue !== null
                              ? `≤ ${param.maxValue} ${param.unit || ''}`
                              : 'No range configured'}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {param.type === 'PASS_FAIL' && (
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="radio"
                                name={`pf-${param.id}`}
                                checked={st.value === 'PASS'}
                                onChange={() => handleValueChange(param.id, 'PASS')}
                                className="text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-sm text-emerald-700 font-medium">Pass</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="radio"
                                name={`pf-${param.id}`}
                                checked={st.value === 'FAIL'}
                                onChange={() => handleValueChange(param.id, 'FAIL')}
                                className="text-red-600 focus:ring-red-500"
                              />
                              <span className="text-sm text-red-700 font-medium">Fail</span>
                            </label>
                          </div>
                        )}
                        {param.type === 'NUMERIC' && (
                          <div className="space-y-2">
                            <input
                              type="number"
                              step="0.01"
                              value={st.value}
                              onChange={(e) => handleValueChange(param.id, e.target.value)}
                              placeholder={`Enter value${param.unit ? ` (${param.unit})` : ''}`}
                              className={`w-full px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 ${
                                isOutOfRange
                                  ? 'border-red-300 focus:ring-red-500 bg-red-50'
                                  : 'border-gray-300 focus:ring-emerald-500'
                              }`}
                            />
                            {isOutOfRange && (
                              <p className="text-xs text-red-600">Value out of specified range</p>
                            )}
                          </div>
                        )}
                        {param.type === 'TEXT' && (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={st.value}
                              onChange={(e) => handleValueChange(param.id, e.target.value)}
                              placeholder="Enter observation"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-1 text-xs cursor-pointer">
                                <input
                                  type="radio"
                                  name={`text-${param.id}`}
                                  checked={(st.manualStatus || 'PASS') === 'PASS'}
                                  onChange={() => handleManualStatus(param.id, 'PASS')}
                                  className="text-emerald-600"
                                />
                                <span className="text-emerald-700 font-medium">Pass</span>
                              </label>
                              <label className="flex items-center gap-1 text-xs cursor-pointer">
                                <input
                                  type="radio"
                                  name={`text-${param.id}`}
                                  checked={st.manualStatus === 'FAIL'}
                                  onChange={() => handleManualStatus(param.id, 'FAIL')}
                                  className="text-red-600"
                                />
                                <span className="text-red-700 font-medium">Fail</span>
                              </label>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            status === 'PASS'
                              ? 'bg-emerald-100 text-emerald-700'
                              : status === 'FAIL'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {parameters.length === 0 && (
            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200 mt-2">
              <i className="ri-file-list-3-line text-2xl mb-1 text-gray-400 block" />
              <p className="text-sm font-medium">Standard quality checklist verification</p>
              <p className="text-xs text-gray-400 mt-0.5">Please select the overall outcome below</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-lg">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Set Overall Result:</span>
            <button
              type="button"
              onClick={() => setManualOverall('PASSED')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors cursor-pointer border ${
                overallStatus === 'PASSED'
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                  : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
              }`}
            >
              <i className="ri-check-line mr-1" /> PASSED
            </button>
            <button
              type="button"
              onClick={() => setManualOverall('FAILED')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors cursor-pointer border ${
                overallStatus === 'FAILED'
                  ? 'bg-red-600 text-white border-red-600 shadow-sm'
                  : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
              }`}
            >
              <i className="ri-close-line mr-1" /> FAILED
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isComplete}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white whitespace-nowrap cursor-pointer ${
                isComplete ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              Submit Inspection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}